import { Outlet } from 'react-router';
import { useReadQuery } from '@apollo/client/react';
import { debounce } from 'lodash';
import { useEffect, useTransition } from 'react';

import { InputSearch } from '@/components/ui/input-search';
import { Main } from 'src/components/Layout';
import { useLocalStorage, usePageMeta } from 'src/hooks';
import { cn } from 'src/lib/utils';
import { SOFTWARE_ESCROWS_FILTER_STORAGE_KEY } from 'src/shared/constants';

import { DashboardContextProvider, useDashboard } from './model';
import { NewEscrowAction, Intro, RemoveContainer, ListContainer, FilterSelect } from './ui';

const Dashboard = () => {
  const [isPending, startTransition] = useTransition();
  const { setQuery, queryRef, status, query } = useDashboard();
  const {
    data: { softwareEscrows },
  } = useReadQuery(queryRef);

  const isEmpty = !softwareEscrows.nodesCount && status === 'all' && !query;

  const debouncedOnSearch = debounce((e) => startTransition(() => setQuery(e.target.value)), 500);

  const onClear = () => setQuery('');

  return (
    <Main title="Software Escrows" noPaddingOnContent headerClassName="border-b-0">
      <div className="flex flex-col h-full bg-background-secondary">
        <div className="bg-background border-b border-border py-4 sm:py-6 shrink-0">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="flex justify-between items-center gap-6">
              <div className="flex items-center flex-row-reverse sm:flex-row gap-2 sm:gap-3 grow">
                <InputSearch
                  className="md:max-w-[320px] w-full"
                  placeholder="Search"
                  onChange={!isEmpty ? debouncedOnSearch : undefined}
                  onClear={onClear}
                  isLoading={isPending}
                />
                <FilterSelect />
              </div>
              <NewEscrowAction />
            </div>
          </div>
        </div>
        <div className="grow overflow-y-auto">
          <div className={cn('container mx-auto relative p-4 sm:p-6 min-h-full', isEmpty && 'h-full')}>
            {isEmpty ? <Intro /> : <ListContainer />}
          </div>
        </div>
      </div>
    </Main>
  );
};

export default function SoftwareEscrowsPage() {
  usePageMeta('Software Escrows');
  const [, setSavedStatus] = useLocalStorage(SOFTWARE_ESCROWS_FILTER_STORAGE_KEY, 'all');
  // set default status to all when the page is loaded
  useEffect(() => {
    setSavedStatus('all');
  }, []);

  return (
    <DashboardContextProvider>
      <RemoveContainer />
      <Dashboard />
      <Outlet />
    </DashboardContextProvider>
  );
}
